import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

/**
 * The organization the platform operator is looking at.
 *
 * Carried on every request as a header, which is why it is applied to the axios
 * defaults at module load rather than in an effect: the very first call this
 * app makes is /api/auth/me, and it has to already be scoped or the nav would
 * show one organization while the pages below showed all of them.
 *
 * It is a view preference, so it lives in localStorage. The server ignores it
 * for anyone but the operator, so a stale or tampered value cannot move an
 * ordinary user out of their own organization.
 */
export const ACTING_ORG_HEADER = 'X-Acting-Organization';
const ACTING_ORG_KEY = 'smarthire.actingOrganizationId';

const readStoredOrg = () => {
  try {
    return window.localStorage.getItem(ACTING_ORG_KEY) || null;
  } catch {
    // Private browsing, or storage disabled. Not being able to remember the
    // selection is survivable; failing to render is not.
    return null;
  }
};

const storeOrg = (id) => {
  try {
    if (id) window.localStorage.setItem(ACTING_ORG_KEY, id);
    else window.localStorage.removeItem(ACTING_ORG_KEY);
  } catch {
    /* as above */
  }
};

const applyHeader = (id) => {
  if (id) axios.defaults.headers.common[ACTING_ORG_HEADER] = id;
  else delete axios.defaults.headers.common[ACTING_ORG_HEADER];
};

applyHeader(readStoredOrg());

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Starts true so protected routes wait rather than bouncing to /login on reload.
  const [loading, setLoading] = useState(true);
  const [actingOrgId, setActingOrgId] = useState(readStoredOrg);

  const refresh = useCallback(async () => {
    try {
      const response = await axios.get('/api/auth/me');
      setUser(response.data.user);
      return response.data.user;
    } catch (error) {
      // A remembered organization that has since been deleted would otherwise
      // make every request fail and leave the app unusable. Drop the selection
      // and carry on with the platform-wide view.
      if (error.response?.data?.code === 'UNKNOWN_ACTING_ORGANIZATION') {
        storeOrg(null);
        applyHeader(null);
        setActingOrgId(null);
        try {
          const retry = await axios.get('/api/auth/me');
          setUser(retry.data.user);
          return retry.data.user;
        } catch {
          setUser(null);
          return null;
        }
      }
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post('/api/auth/login', { email, password });
    setUser(response.data.user);
    return response.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post('/api/auth/logout');
    } finally {
      storeOrg(null);
      applyHeader(null);
      setActingOrgId(null);
      setUser(null);
    }
  }, []);

  /** Narrow everything below the nav to one organization, or to none of them. */
  const selectOrganization = useCallback(
    async (id) => {
      const next = id || null;
      storeOrg(next);
      applyHeader(next);
      setActingOrgId(next);
      await refresh();
    },
    [refresh]
  );

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      refresh,
      actingOrgId,
      actingOrganization: user?.actingOrganization || null,
      selectOrganization
    }),
    [user, loading, login, logout, refresh, actingOrgId, selectOrganization]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }
  return context;
};

export default AuthContext;
